import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Chip, Divider, Paper, Typography, Avatar, IconButton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { 
  IconCalendar,
  IconHistory,
  IconUserCircle,
  IconMail,
  IconWorld,
  IconStack2,
  IconClipboardList,
  IconX,
  IconAutomation,
  IconBriefcase
} from '@tabler/icons-react';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
const DetailItem = ({ icon: Icon, label, value }) => {
  return (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
    <Box sx={{ color: '#64748b', mr: 2, mt: 0.2, display: 'flex' }}>
      <Icon size={20} stroke={1.5} />
    </Box>
    <Box>
      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', lineHeight: 1.2, mb: 0.5, fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 500 }}>
        {value || '-'}
      </Typography>
    </Box>
  </Box>
  );
};

DetailItem.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const SectionTitle = ({ title }) => (
  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b', mb: 1.5 }}>
    {title}
  </Typography>
);

SectionTitle.propTypes = {
  title: PropTypes.string.isRequired,
};

const RequestInfoCard = React.forwardRef(({ selectedRequest, onProcessRequest, onClose, maxHeight, isShortTable }, ref) => {
  return (
    <Paper
      ref={ref}
      elevation={0}
      sx={{
        p: 0,
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        backgroundColor: '#ffffff',
        boxShadow: '0 8px 32px rgba(0,0,0,0.04)',
        height: 'auto',
        maxHeight: maxHeight 
          ? { xs: 'auto', md: maxHeight } 
          : { xs: 'auto', md: 'calc(100vh - 120px)' },
        minHeight: { xs: 'auto', md: isShortTable ? 'calc(100vh - 120px)' : 0 },
        display: 'flex',
        flexDirection: 'column',
        position: { xs: 'static', md: 'sticky' },
        top: { md: 24 },
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box sx={{
        px: 2.5,
        py: 2,
        borderBottom: '1px solid #f1f5f9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: '#fff'
      }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b' }}>
          Request Inspector
        </Typography>
        {onClose && (
          <IconButton size="small" onClick={onClose} sx={{ color: '#64748b', p: 0.5 }} title="Close">
             <IconX size={20} stroke={1.5} />
          </IconButton>
        )}
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
        {!selectedRequest ? (
          <Box sx={{ py: 10, textAlign: 'center' }}>
            <Box sx={{ color: alpha('#e2e8f0', 0.8), mb: 1.5, display: 'flex', justifyContent: 'center' }}>
              <IconStack2 size={40} stroke={1.5} />
            </Box>
            <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 500 }}>Select a request to view details</Typography>
          </Box>
        ) : (
          <Box>
            {/* User Info Block */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <Avatar sx={{ bgcolor: '#f1f5f9', color: '#1e293b', mr: 2, width: 44, height: 44 }}>
                <PersonOutlineIcon />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b', lineHeight: 1.2 }}>
                  {selectedRequest.accountName || 'Unknown Account'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                  MDM ID: {selectedRequest.accountId || '-'}
                </Typography>
              </Box>
              {selectedRequest.status && (
                <Chip
                  label={selectedRequest.status}
                  size="small"
                  sx={{
                    bgcolor: '#eff6ff',
                    color: '#3b82f6',
                    fontWeight: 700,
                    height: 24,
                    
                    borderRadius: 1
                  }}
                />
              )}
            </Box>

            <Divider sx={{ borderColor: '#f1f5f9', mb: 1.2 }} />

            <SectionTitle title="Request Details" />
            <DetailItem icon={IconAutomation} label="Processing Engine" value={selectedRequest?.processingEngine} />
            <DetailItem icon={IconCalendar} label="Request Date" value={selectedRequest?.createdDate} />
            <DetailItem icon={IconHistory} label="Last Update" value={selectedRequest?.lastModifiedDate} />

            <Divider sx={{ borderColor: '#f1f5f9', my: 1.5 }} />

            <SectionTitle title="Account Info" />
            <DetailItem icon={IconBriefcase} label="MDM ID" value={selectedRequest?.accountId} />
            <DetailItem icon={IconStack2} label="Entity Type" value={selectedRequest?.mdmAccountType} />

            <Divider sx={{ borderColor: '#f1f5f9', my: 1.5 }} />

            <SectionTitle title="Requestor" />
            <DetailItem icon={IconUserCircle} label="Name" value={selectedRequest?.ownerDetails?.name} />
            <DetailItem icon={IconMail} label="Email" value={selectedRequest?.ownerDetails?.email} />
            <DetailItem icon={IconWorld} label="Territory" value={selectedRequest?.ownerDetails?.primTerritory} />

            <Divider sx={{ borderColor: '#f1f5f9', my: 1.5 }} />

            <SectionTitle title="Analysis" />
            <DetailItem icon={IconClipboardList} label="Fields Involved" value={Array.isArray(selectedRequest?.fieldName) ? selectedRequest.fieldName.join(', ') : '-'} />
          </Box>
        )}
      </Box>

      {/* Sticky Footer */}
      {selectedRequest && (
        <Box sx={{ p: 2.5, bgcolor: '#fff', borderTop: '1px solid #f1f5f9' }}>
          <Button className="action-btn process-btn"
            fullWidth
            variant="contained"
            disableElevation
            onClick={() => onProcessRequest(selectedRequest)}
            sx={{
              py: 1.2,
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 700,
              
              backgroundColor: '#2196f3',
              '&:hover': { backgroundColor: '#1565C0' },
            }}
          >
            Process Request
          </Button>
        </Box>
      )}
    </Paper>
  );
});

RequestInfoCard.displayName = 'RequestInfoCard';

RequestInfoCard.propTypes = {
  selectedRequest: PropTypes.object,
  onProcessRequest: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  maxHeight: PropTypes.string,
  isShortTable: PropTypes.bool,
};

export default RequestInfoCard;
